import React from 'react';

export interface DownloadOverlayProps {
  visible: boolean;
  message: string;
  canDownload: boolean;
  downloadUrl?: string;
  title?: string;
  fileTypeName?: string;
  fileSize?: number;
  fileIconUrl?: string;
  /** When set, a "Switch to Proxy" button is shown so the user can fall back
   *  to a browser-playable proxy instead of the unplayable original. */
  onSwitchToProxy?: () => void;
}

/**
 * Format a byte count into a human-readable string.
 * e.g. 1048576 → "1.0 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Download/error overlay — recreates the PHP `createUnableToPlayMessage()`
 * as a React component. Uses the existing `.download-box` CSS classes from
 * `sass/player/download.scss`.
 *
 * Two modes:
 * - canDownload=false → dark box with logo + error message (not clickable)
 * - canDownload=true  → gradient box linked to downloadUrl with file info
 */
export const DownloadOverlay = React.memo(function DownloadOverlay(props: DownloadOverlayProps) {
  const {
    visible,
    message,
    canDownload,
    downloadUrl,
    title,
    fileTypeName,
    fileSize,
    fileIconUrl,
    onSwitchToProxy,
  } = props;

  if (!visible) return null;

  const iconSrc = fileIconUrl || '/img/Unknown_Doc.svg';

  if (!canDownload) {
    return (
      <div
        className="kollab-player-download-overlay kollab-player-download-overlay--visible"
        role="alert"
        aria-label="Playback error"
      >
        <div className="download-container">
          <div className="download-box dark">
            <div className="logo white" style={{ height: 100 }} />
            <div className="info-container" style={{ bottom: '25%' }}>
              <h2>Cannot display file</h2>
              <span style={{ fontSize: '15pt' }}>{message}</span>
              {onSwitchToProxy && (
                <button
                  className="kollab-player-switch-proxy-btn"
                  onClick={onSwitchToProxy}
                  type="button"
                >
                  Switch to Proxy
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inner = (
    <div className="download-box">
      <div className="logo" />
      <div className="info-container">
        <h1>{title || 'Download'}</h1>
        <p>
          <img src={iconSrc} style={{ width: 80 }} alt="" />
        </p>
        <p>
          {fileTypeName || 'File'}
          {fileSize != null && (
            <>
              <br />
              {formatFileSize(fileSize)}
            </>
          )}
        </p>
      </div>
      <div className="download-label">Click to download</div>
    </div>
  );

  return (
    <div
      className="kollab-player-download-overlay kollab-player-download-overlay--visible"
      role="alert"
      aria-label="Download available"
    >
      <div className="download-container">
        {downloadUrl ? (
          <a href={downloadUrl}>{inner}</a>
        ) : (
          inner
        )}
      </div>
    </div>
  );
});
